import editorStyle from "./reportWriter.module.css";
import commonStyle from "../commonStyles.module.css";
import logo from "../../../../assets/PentraLogoDarkFull.png";

import { useState, useRef, useCallback, useContext, useMemo, useEffect } from "react";
import { apiVulnerabilitiesUpdate } from "../../../../sdk.ts";

import Section, { SectionData } from "./components/Section/Section.tsx";

import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Terminal, Trash2 } from "lucide-react";

import { AuthContext } from "../../../../contexts/AuthContext.ts";
import { PentestContext } from "../../../../contexts/PentestContext.ts";
import VulnSections from "./components/VulnSections/VulnSections.tsx";
import { EditorContext } from "../../../../contexts/EditorContext.ts";
import { AppContext } from "../../../../contexts/AppContext.ts";
import axios from "axios";
import EditorWidget from "./components/EditorWidget/EditorWidget.tsx";
import { addVuln } from "../../../Common/createVuln.ts";
import GeneratingSection from "./components/GeneratingSection/GeneratingSection.tsx";
import { ChangeVuln, Vulnerability } from "../../../../sdkStructs.ts";

const defaultSection: SectionData = {
  id: -1,
  text: "",
  saveFunc: () => {},
};

function ReportWriter() {
  const navigate = useNavigate();
  const token = useContext(AuthContext).token;

  const { pentestId, pentest, currentWindow, pentestVulns, refreshVulns, generating, scrollToVuln, vulnRefs, confirmRemoveVuln, openVulnModal } =
    useContext(PentestContext);
  const { setShowModal } = useContext(AppContext);
  // @ts-expect-error loading is unused
  const [loading, setLoading] = useState(false); // TODO: use loading to display some indication to the user that the changes are saved

  const [value, setValue] = useState("");
  const [editSection, setEditSection] = useState<SectionData>(defaultSection);

  const [searchParams] = useSearchParams({ window: "logger", vulnId: "0" });
  const vulnId = searchParams.get("vulnId");

  const [hasScrolled, setHasScrolled] = useState(false);

  function enableNameModal() {
    setShowModal("name", {
      title: "Vuln Name",
      submit: createNewVuln,
      close: () => setShowModal("none", undefined),
    });
  }

  useEffect(() => {
    if (hasScrolled) return;
    const timeout = setTimeout(() => {
      setHasScrolled(true);
      if (pentestVulns.length > 0 && vulnId) {
        const index1Based = parseInt(vulnId, 10);
        // Convert from 1-based (URL) to 0-based (array indexing)
        const index0Based = index1Based - 1;

        // Ensure it's a valid index in the array
        if (!isNaN(index0Based) && index0Based >= 0 && index0Based < pentestVulns.length) {
          scrollToVuln(index0Based);
        }
      }
    }, 1500);

    return () => clearTimeout(timeout);
  }, [pentestVulns.length, vulnId, hasScrolled, scrollToVuln]);

  useEffect(() => {
    if (!token || pentestId === -1) {
      navigate("/");
    }
  }, [token, pentestId, navigate]);

  const handleClose = () => setEditSection(defaultSection);

  const saveVulnChanges = useCallback(
    async (vulnChanges: ChangeVuln) => {
      setLoading(true);
      try {
        await apiVulnerabilitiesUpdate(token, vulnChanges);
        await refreshVulns();
      } catch (error) {
        if (!axios.isAxiosError(error)) {
          return;
        }
        console.log("ERROR SAVING VULN:", error.response);
      }
      setLoading(false);
    },
    [token, refreshVulns, setLoading],
  );

  const contextValue = useMemo(() => {
    return {
      saveVulnChanges: saveVulnChanges,
      value,
      setValue,
    };
  }, [saveVulnChanges, value]);

  const myRef = useRef<HTMLDivElement>(null);
  const executeScroll = () => {
    setTimeout(function () {
      if (myRef.current) {
        const elementPosition = myRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - 150; // offset from the top

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 10);
  };

  function selectEditSection(data: SectionData) {
    setValue(data.text);
    setEditSection(data);

    executeScroll();
  }

  function confirmSelectEditSection(data: SectionData) {
    if (editSection.id === -1 || editSection.text === value) {
      selectEditSection(data);
      return;
    }

    saveSection(value); // TODO: when selecting new section it loses changes if you don't wait for the debounce to pass
    selectEditSection(data);
  }

  function cancelChanges() {
    handleClose();
  }
  function confirmCancelChanges() {
    if (editSection.id === -1 || editSection.text === value) {
      cancelChanges();
      return;
    }

    setShowModal("confirm", {
      text: "Are you sure you want to close the editor? You will lose all edits since last save.",
      confirm: async () => {
        cancelChanges();
      },
      close: () => setShowModal("none", undefined),
    });
  }

  function saveSection(finalValue: string) {
    editSection.saveFunc(finalValue);
    handleClose();
  }

  async function createNewVuln(name: string) {
    console.log(name);
    setLoading(true);
    try {
      await addVuln(token, [], name, pentestId);
      await refreshVulns();
    } catch (e) {
      console.log(e);
      if (!axios.isAxiosError(e)) return;
      console.log("ERROR ADDING NEW VULNERABILITY:", e.response);
    }
    setLoading(false);
  }

  function getOneSection(section: SectionData) {
    if (section.id === editSection.id)
      return <EditorWidget isCvss={section.isCvss ?? false} confirmCancelChanges={confirmCancelChanges} saveSection={saveSection} scrollRef={myRef} />;

    return <Section key={section.id} data={section} selectFun={confirmSelectEditSection} />;
  }

  function getSections() {
    if (!pentest) return;

    return (
      <>
        {Array.from({ length: pentestVulns.length }, (_, i: number) => {
          const vuln = pentestVulns[i];
          if (generating[vuln.ID]) return <GeneratingSection key={i} id={i + 1} />;
          return (
            <VulnSections
              key={i}
              scrollRef={(el) => (vulnRefs.current ? (vulnRefs.current[i] = el) : el)}
              vuln={pentestVulns[i]}
              getOneSection={getOneSection}
              id={i}
            />
          );
        })}
      </>
    );
  }

  if (currentWindow !== "editor") return;
  const editingClassName: string = editorStyle.documentRegionEditing;

  return (
    <EditorContext.Provider value={contextValue}>
      <div className={editorStyle.wrapper}>
        <div className={commonStyle.contentRegion}>
          <a href="/">
            <img
              className={commonStyle.logoImage + " " + commonStyle.uglyFix}
              src={logo}
              key={logo}
              alt="Pentra Logo"
            />
          </a>

          {pentestVulns.map((vuln: Vulnerability, number: number) => {
            return (
              <div key={vuln.ID} className={editorStyle.verticalSplit}>
                <div className={editorStyle.lateralButtons}>
                  <button className={editorStyle.lateralButton + " " + editorStyle.borderBottomButton} onClick={() => confirmRemoveVuln(vuln.ID)}>
                    <Trash2 className={editorStyle.lateralIcons} />{" "}
                  </button>
                  <button className={editorStyle.lateralButton + " " + editorStyle.borderTopButton} onClick={() => openVulnModal(vuln, number + 1)}>
                    <Terminal className={editorStyle.lateralIcons} />{" "}
                  </button>
                </div>
                <button
                  onClick={() => {
                    scrollToVuln(number);
                  }}
                  className={editorStyle.contentVulnWrapper}
                >
                  <span className={editorStyle.onedarkComment}>{number + 1 + ". "}</span>
                  {vuln.Name}
                  {vuln.Tag && <div className={editorStyle.vulnTag}>{vuln.Tag}</div>}
                </button>
              </div>
            );
          })}
        </div>
        <div className={commonStyle.contentRegionPadding} />
        <div className={editorStyle.documentRegion + " " + editingClassName}>
          <h3 className={editorStyle.pentestTitle}>
            {pentest ? pentest.Title : "Loading ..."}
            <button
              className={editorStyle.addVulnButtonTop}
              onClick={() => {
                enableNameModal();
              }}
            >
              <Plus className={editorStyle.plusIcon} />
              <span className={editorStyle.normalText}>Add Vuln</span>
            </button>
          </h3>

          <div className={editorStyle.document}>{getSections()}</div>

          {pentestVulns.length > 0 && (
            <button
              className={editorStyle.addVulnButton}
              onClick={() => {
                enableNameModal();
              }}
            >
              <Plus className={editorStyle.plusIcon + " " + editorStyle.plusIconText} />
              Add Vulnerability
            </button>
          )}
          <div className={editorStyle.footer} />
        </div>
      </div>
    </EditorContext.Provider>
  );
}

export default ReportWriter;
